import { useLocation } from 'react-router'
import { useNavigateWithParams } from '@/hooks/use-navigate-with-params'

/**
 * Where each screen's back button goes.
 *
 * `history.back()` is wrong whenever the screen was not reached by walking the
 * app: a pasted URL, a lesson step that deep-links straight here, or a redirect
 * all leave either no previous entry or one belonging to a different flow. A
 * declared parent always lands somewhere coherent.
 *
 * Only routes whose parent is NOT simply "drop the last segment" are listed —
 * `parentOf` derives the rest. Keys are route patterns as written in
 * `routes.tsx`, so `:id` matches any single segment.
 *
 * A value may be a plain path string or a `[path, extraParams]` tuple when the
 * parent needs additional query params that the child's URL doesn't carry.
 */
type ParentTarget = string | [string, Record<string, string>]
const PARENT_OVERRIDES: Record<string, ParentTarget> = {
  // Tasks live under the home screen, not under a /tasks index (there is none).
  // `/tasks/:id/start` and `/tasks/:id/edit` derive correctly to `/tasks/:id`.
  '/tasks/:id': '/home',
  '/tasks/all': '/home',
  '/tasks/create': '/home',
  '/tasks/store-tasks/search': '/tasks/all',

  // Dropping the last segment would land on `/expiry/detail` or `/ordering/qpo`
  // — neither of which is a route.
  '/expiry/detail/:id': '/expiry',
  '/expiry/write-off': '/expiry',
  '/stocktake/search': '/stocktake/scan-sku',
  '/stocktake/setup/sku-search': ['/stocktake/setup', { tab: 'progress' }],
  '/stocktake/scan-sku': '/stocktake/scan-location',
  '/stocktake/scan-list': '/stocktake/scan-sku',
  '/ordering/qpo/scan': '/orders',
  '/ordering/qpo/sku-scan': '/orders',
  '/ordering/qpo/sku-search': '/orders',
  '/ordering/qpo/supplier': '/orders',
  '/ordering/qpo/verify': '/orders',
  '/ordering/qpo/summary': '/orders',
  '/ordering/qpo/success': '/orders',
  '/ordering/qpo/shortfall-claims': '/orders',
  '/ordering/qpo/shortfall-success': '/orders',

  // A finished lesson belongs to the dashboard, not to its own start screen.
  '/lesson/:subModuleId/complete': '/',
  '/lesson/:subModuleId/guided-complete': '/',

  // The returns flow is entered from the To Return tab, not from /returns.
  '/returns': '/features',
  '/returns/pending': '/orders',
  '/returns/rsto': '/orders',
  '/dispose': '/orders',
  '/issue-goods': '/orders',

  // Receiving and ordering are entered from the Orders tabs.
  '/orders/:id': '/orders',
  '/receiving/:id': '/orders',
  '/receiving/do/:id': '/orders',
  '/receiving/print-slip': '/history',
  '/receiving/shortfall-claims': '/orders',
  '/receiving/shortfall-success': '/orders',
  '/receiving/success': '/orders',
  '/ordering/qpo/supplier/search': '/ordering/qpo/supplier',

  // Top-level destinations reached from the menu.
  '/stocktake/setup': '/features',
  '/orders': '/features',
  '/history': '/features',
  '/expiry': '/features',
  '/stocktake': '/features',
  '/soh-adjustment': '/features',
  '/articlepedia': '/home',
  '/cart': '/orders',
  '/scan': '/home',
  '/features': '/home',
  '/accessibility': '/home',
  '/gallery': '/home',

  // History detail screens all belong to the one list.
  '/history/:id': '/history',
  '/history/adj/:id': '/history',
  '/history/gi/:id': '/history',

  // Dashboard.
  '/required-training': '/',
  '/module/:id': '/',
  '/lesson-detail/:id': '/',
  '/lesson/:subModuleId': '/',
}

/** Does a concrete pathname match a route pattern with `:param` segments? */
function matches(pathname: string, pattern: string): boolean {
  const a = pathname.split('/').filter(Boolean)
  const b = pattern.split('/').filter(Boolean)
  if (a.length !== b.length) return false
  return b.every((seg, i) => seg.startsWith(':') || seg === a[i])
}

/**
 * The route a back button should land on from `pathname`.
 *
 * An override wins; otherwise drop the last segment, which is right for the
 * many `/<flow>/<step>` screens. Anything with nowhere left to drop goes home.
 */
export function parentOf(pathname: string): ParentTarget {
  let clean = pathname
  while (clean.endsWith('/')) clean = clean.slice(0, -1)
  clean = clean || '/'
  for (const [pattern, parent] of Object.entries(PARENT_OVERRIDES)) {
    if (matches(clean, pattern)) return parent
  }
  const segments = clean.split('/').filter(Boolean)
  if (segments.length <= 1) return '/home'
  return `/${segments.slice(0, -1).join('/')}`
}

/**
 * A back handler that lands on the screen's parent rather than wherever the
 * history stack happens to point, carrying `?tour=` / `?sim=` so a learner
 * mid-lesson stays in it.
 */
export function useGoBack(): (fallback?: string) => void {
  const { pathname } = useLocation()
  const navigateTo = useNavigateWithParams()
  return (fallback?: string) => {
    const target = fallback ?? parentOf(pathname)
    if (Array.isArray(target)) {
      navigateTo(target[0], target[1])
    } else {
      navigateTo(target)
    }
  }
}
